/* eslint-disable no-plusplus */
import { MultipartInput } from '@generated/graphql';
import { delay } from '@helper-functions/delay';
import { runPromiseQueue } from '@helper-functions/promiseQueue';
import axios, { AxiosProgressEvent } from 'axios';

const MAX_RETRIES = 3;

type UploadFileProps = {
  file: File;
  urls: string[];
  partSize: number;
  onUploadProgress?: (percent: number) => void;
};

type UploadPart = {
  url: string;
  part: Blob;
  partNumber: number;
  retries: number;
};

export const uploadFile = async ({
  file,
  urls,
  partSize,
  onUploadProgress,
}: UploadFileProps): Promise<MultipartInput[]> => {
  if (!file || !urls.length) return [];

  const etags: MultipartInput[] = [];
  const fileParts: Blob[] = [];
  const uploadProgress: number[] = [];
  if (partSize === 0 || urls.length === 1) {
    fileParts.push(file);
  } else {
    for (let i = 0; i < urls.length; i++) {
      fileParts.push(file.slice(i * partSize, (i + 1) * partSize));
      uploadProgress.push(0);
    }
  }

  const uploadPart = async ({ url, part, partNumber, retries }: UploadPart) =>
    axios
      .put(url, part, {
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            uploadProgress[partNumber - 1] = progressEvent.loaded / progressEvent.total;
            const totalProgress = uploadProgress.reduce((acc, curr) => acc + curr, 0) / urls.length;
            onUploadProgress(totalProgress);
          }
        },
      })
      .then((response) => {
        etags.push({
          eTag: (response?.headers as { etag: string })?.etag,
          partNumber,
        });
      })
      .catch(async () => {
        // eslint-disable-next-line no-param-reassign
        retries++;
        if (retries === MAX_RETRIES) {
          throw new Error(`Failed to upload file.`);
        } else {
          await delay(5000);
          await uploadPart({ url, part, partNumber, retries });
        }
      });

  await runPromiseQueue(
    urls.map(
      (url, index) => () =>
        uploadPart({ url, part: fileParts[index], partNumber: index + 1, retries: 0 }),
    ),
  );

  return etags.sort((a, b) => a.partNumber - b.partNumber);
};

// Backwards-compat hook — returns the module-level uploadFile so consumers
// don't need to change. The reference is permanently stable.
export const useFileUpload = () => uploadFile;
